import 'wicg-inert';

/*
// Bootstrap
import 'bootstrap/js/src/collapse';
import 'bootstrap/js/src/dropdown';
*/
import i18n from '@i18n';
import axios from 'axios';
import BootstrapVue from 'bootstrap-vue';
import { createPinia, PiniaVuePlugin, setMapStoreSuffix } from 'pinia';
import PortalVue from 'portal-vue';
import { configureCompat, createApp } from 'vue';
import VueGtm from 'vue-gtm';
import VueQuillEditor from 'vue-quill-editor';
import Toastr from 'vue-toastr';

import router from '@routes';

import authService from '@services/auth.service';

import store from '@store';
import { useModalStore } from '@store/pinia-utils/refresh';

import clickOutsideDirective from '@common/directives/click-outside.js';
import filters from '@common/filters';
import validators from '@common/validators.js';

import BaseBadges from '@components/BaseBadges';
import BaseCheckbox from '@components/BaseCheckbox';
import FdDatePicker from '@components/BaseDatePicker/fd-base-date-picker/fd-date-picker';
import BaseDelete from '@components/BaseDelete';
import BaseFilePreview from '@components/BaseFilePreview/BaseFilePreview';
import BaseInput from '@components/BaseInput';
import BaseRadio from '@components/BaseRadio';
import BaseSelect from '@components/BaseSelect';
import BaseTable from '@components/BaseTable/BaseTable.vue';
import Collapse from '@components/Collapse';
import InvalidIndicator from '@components/InvalidIndicator';
import BreadcrumbMenu from '@components/layout/BreadcrumbMenu';
import IntroductionBody from '@components/layout/IntroductionBody';
import Marker from '@components/Marker';
import MultilingualInput from '@components/MultilingualInput';
import MultilingualQuill from '@components/MultilingualQuill';
import MultilingualTextarea from '@components/MultilingualTextarea';
import FdProgressBar from '@components/ProgressBar/fd-progressbar/fd-progress-bar';
import FdComponent from '@components/web-component-example/fd-component';

import App from '@/App.vue';
import Rollbar from '@/rollbar';
import FdTimelineChart from '@views/ActivityLog/Timeline/TimelineChart/fd-timeline-chart/fd-timeline-chart';
import FdOverviewRadialChart from '@views/Overview/components/fd-overview-radial-chart/fd-overview-radial-chart';

console.info(`%cApp version is: ${process.env.APP_VERSION}`, 'background: #222; color: #fff; padding: 10px;');

validators.init();

setMapStoreSuffix('Store');

const pinia = createPinia();

axios.defaults.baseURL = process.env.API_URL;

export let vm, app;

// For bootstrap-vue...
configureCompat({ MODE: 2 });

app = createApp(App);
setupApp(app);

async function presetAppData() {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('shared_access_token');

    if (token) {
        await store.dispatch('auth/setSharedToken', token);
    }

    if (authService.isAuthorized || authService.isSharedLink) {
        return await store.dispatch('initApp');
    }
}

async function setupApp(app) {
    app.use(store);
    app.use(pinia);
    await presetAppData();
    app.use(router);
    app.use(i18n);
    // app.config.globalProperties.$t = i18n.global.t;
    app.$i18n = i18n.global;
    app.$t = i18n.global.t;
    app.$store = store;
    app.$router = router;

    app.use(BootstrapVue);
    app.use(PortalVue);
    app.use(PiniaVuePlugin);
    const toolbarOptions = [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ['link', 'image', 'video'],
    ];

    app.use(VueQuillEditor, {
        modules: {
            toolbar: toolbarOptions,
        },
    });

    if (process.env.MIX_APP_ENV === 'production' || process.env.MIX_APP_ENV === 'staging') {
        app.use(Rollbar, {
            accessToken: process.env.MIX_ROLLBAR_TOKEN,
            captureUncaught: true,
            captureUnhandledRejections: true,
            enabled: true,
            source_map_enabled: true,
            payload: {
                environment: process.env.MIX_APP_ENV,
                client: {
                    javascript: {
                        code_version: process.env.APP_VERSION,
                    },
                },
            },
        });
    }

    app.use(Toastr, {
        defaultTimeout: 10000,
        defaultPosition: 'toast-top-center',
        defaultCloseOnHover: false,
        defaultPreventDuplicates: true,
        defaultProgressBar: false,
    });

    app.use(VueGtm, {
        id: [process.env.GTM_ID],
        queryParams: (() => {
            return {
                production: {
                    gtm_auth: process.env.GTM_PROD_AUTH,
                    gtm_preview: process.env.GTM_PROD_PREVIEW,
                    gtm_cookies_win: process.env.GTM_PROD_COOKIES_WIN,
                },
                development: {
                    gtm_auth: process.env.GTM_DEV_AUTH,
                    gtm_preview: process.env.GTM_DEV_PREVIEW,
                    gtm_cookies_win: process.env.GTM_DEV_COOKIES_WIN,
                },
            }[process.env.NODE_ENV];
        })(),
        enabled: true,
        debug: process.env.NODE_ENV === 'development',
        loadScript: false,
        vueRouter: router,
        ignoredViews: [],
    });
    app.component('BaseFilePreview', BaseFilePreview);
    app.component('BaseSelect', BaseSelect);
    // app.component('Field', Field);
    // app.component('Form', Form);
    app.component('MultilingualTextarea', MultilingualTextarea);
    app.component('MultilingualInput', MultilingualInput);
    app.component('MultilingualQuill', MultilingualQuill);
    app.component('IntroductionBody', IntroductionBody);
    app.component('InvalidIndicator', InvalidIndicator);
    app.component('BreadcrumbMenu', BreadcrumbMenu);
    app.component('Collapse', Collapse);
    app.component('FdMarker', Marker);

    app.component('BaseBadges', BaseBadges);
    app.component('BaseDelete', BaseDelete);
    app.component('BaseRadio', BaseRadio);
    app.component('BaseCheckbox', BaseCheckbox);
    app.component('BaseInput', BaseInput);
    app.component('BaseTable', BaseTable);

    // Common directives
    app.directive('click-outside', clickOutsideDirective);
    app.directive('focus', {
        mounted: function (el, binding, vNode) {
            if (vNode && binding && binding.instance && !binding.instance.val) {
                el.getElementsByTagName('input')[binding.value || 0].focus();
            }
        },
    });

    app.config.globalProperties.$filters = filters;

    const errLogFn = console.error;
    console.error = function (err, ...rest) {
        errLogFn(err, ...rest);

        if (err?.name === 'ChunkLoadError' || err?.message?.includes('chunk')) {
            /**
             * Show new release message with refresh button
             * cause ChunkLoadError appears after a release
             * needs to be after app init for Pinia to exist
             */
            const modalStore = useModalStore();
            modalStore.show();
        } else if (err?.message) {
            const toastr = app.config.globalProperties.$toastr;
            toastr.e(app.$t(err.message ?? err));
        }
    };
    const warnLogFn = console.warn;
    // eslint-disable-next-line no-console
    console.appWarn = function (warn, ...rest) {
        warnLogFn(warn, ...rest);
        const toastr = app.config.globalProperties.$toastr;
        toastr.w(app.$t(warn.message ?? warn));
    };

    vm = app;

    if (window.Cypress) {
        // only available during E2E tests
        window.app = vm;
    }
    app.mount('#app');
}

// vue components
window.Popper = require('popper.js').default;
customElements.define('fd-overview-radial-chart', FdOverviewRadialChart);
customElements.define('fd-progress-bar', FdProgressBar);
customElements.define('fd-timeline-chart', FdTimelineChart);

// web components
customElements.define('fd-component', FdComponent);
customElements.define('fd-date-picker', FdDatePicker);

window.onerror = function (message, url, lineNo, columnNo, error) {
    if (typeof message === 'string' && message.includes('Uncaught SyntaxError: Unexpected token "<"')) {
        alert(vm.$t('New version has been released and application needs to be refreshed'));
        window.location.reload(true);
    } else {
        throw error;
    }
};
